import React, { ReactNode, useCallback, useMemo } from "react";
import { map, sum } from "lodash";
import styled from "styled-components";
import Legend from "@charty/legend";
import { Item } from "@charty/legend/item/model";
import { Serie } from "@charty/models";
import Tooltip from "@charty/tooltip/histogram";
import { Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { useLegendSeries } from "./data";

export interface LegendProps {
  series: Serie[];
  setHovered: (uid: string) => void;
  onClickLegend: (uid: Item["id"] | Item["id"][]) => void;
  tooltip?: (serie: Item, total: number) => ReactNode;
  total?: number;
  range: Range;
  locale: Locale;
  disableAnimation?: boolean;
  displayVolumes?: boolean;
  legendSort?: "alpha" | "volumeDesc";
}

const StyledLegend = styled(Legend)`
  margin-bottom: 12px;
`;

const LineChartLegend = React.memo(
  ({
    series,
    setHovered,
    locale,
    onClickLegend,
    tooltip,
    total,
    range,
    disableAnimation,
    displayVolumes,
    legendSort = "volumeDesc",
  }: LegendProps) => {
    const legendSeries = useLegendSeries(series, legendSort);
    const _total = useMemo(() => {
      if (total) {
        return total;
      } else {
        return sum(map(legendSeries, "count"));
      }
    }, [legendSeries, total]);

    const onSetHovered = useCallback(
      (serieId) => {
        if (serieId !== "other") {
          setHovered(serieId);
        }
      },
      [setHovered]
    );
    return (
      <StyledLegend
        items={legendSeries}
        setHovered={onSetHovered}
        locale={locale}
        onClickSerie={onClickLegend}
        tooltip={
          tooltip
            ? (serie) => tooltip(serie, _total)
            : (serie) => (
                <Tooltip
                  count={serie.count}
                  label={serie.label}
                  color={serie.color}
                  locale={locale}
                  range={range}
                  total={_total}
                />
              )
        }
        disableAnimation={disableAnimation}
        displayVolumes={displayVolumes}
        magicWandCursor
      />
    );
  }
);

export default LineChartLegend;
